import { logger } from '@dealerportal/core';
import { textContent } from '@dealerportal/react-helpers/textContent';
import {
  BROADSTREET_INIT_SCRIPT_SRC,
  GetImageDimensionsFromURLFragment,
  gatedScriptLoader,
  HashStringInsecure,
  isGatedAnalyticsSrc,
  OptimizeCSS,
  ParsedCloudinaryURL,
  toClientMenuItems,
  visible,
  WrapCSSInLayer,
} from '@dealerportal/utilities';
import { addDays } from 'date-fns/addDays';
import {
  attributesToProps,
  type DOMNode,
  domToReact,
  type Element,
  type HTMLReactParserOptions,
} from 'html-react-parser';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import Script from 'next/script';
import React, { type ComponentProps } from 'react';
import { AfterHydration } from './AfterHydration';
import { BundledExternalStyle } from './BundledExternalStyle';
import ClientErrorBoundary from './ClientErrorBoundary';
import {
  AccordionMenu,
  ActonSubmitButton,
  BroadlumeLogo,
  DatePickerInput,
  FaderCarousel,
  FeaturedProducts,
  FormNotes,
  Gallery,
  GoogleMap,
  Masonrygallery,
  Navigation,
  PrintCouponButton,
  ProductSearchBox,
  RecentProducts,
  SourceInput,
  SwiperLink,
  TabComponent,
} from './cmsClientComponents';
import { validateTags } from './FaderCarouselItem';
import {
  type ContentReactParserProps,
  type EnhancedDOMNode,
  Include,
} from './Include';
import { MenuItemBase } from './MenuItemBase';
import { ScriptOnVisible } from './ScriptOnVisible';
import {
  classifyScriptLifecycle,
  makeWithLifecycle,
  NAMED_ID_LIFECYCLE,
} from './scriptLifecycle';

const Accordion = dynamic(() => import('./Accordion'));
const CollapseContainer = dynamic(() => import('./CollapseContainer'));
const FaderCarouselItem = dynamic(() => import('./FaderCarouselItem'));
const GalleryItem = dynamic(() => import('./GalleryItem'));
const ImageMenu = dynamic(() => import('./ImageMenu'));
const Logo = dynamic(() => import('./Logo'));
const PhoneTo = dynamic(() => import('./PhoneTo'));
const TabbedMultiMenu = dynamic(() => import('./TabbedMultiMenu'));
const VerticalMultiMenu = dynamic(() => import('./VerticalMultiMenu'));
const YouTube = dynamic(() => import('./YouTube'));

type TabProps = ComponentProps<typeof TabComponent>;

// HTMLScriptElement types that the browser treats as data, not executable JS.
// We must not run a JS syntax check on these — JSON-LD, templates, etc. are
// allowed to contain things that aren't valid JavaScript.
const NON_JS_SCRIPT_TYPES = new Set([
  'application/json',
  'application/ld+json',
  'text/template',
  'text/x-template',
  'text/html',
  'text/plain',
  'importmap',
  'speculationrules',
]);

export const isJavaScriptScriptType = (
  type: string | undefined | null
): boolean => {
  if (!type) return true;
  const lower = type.trim().toLowerCase();
  if (lower === '') return true;
  return !NON_JS_SCRIPT_TYPES.has(lower);
};

/**
 * Returns true if `src` is parseable as JavaScript.
 *
 * When an inline <script> with invalid syntax is inserted into the DOM, the
 * browser parses it synchronously and throws a SyntaxError that propagates
 * through React's commit-phase appendChild call, taking down the page
 * (DEALERPORTAL-2FQY). The outer try/catch wrapper we apply to CMS scripts
 * cannot catch this — syntax errors fire at parse time, before any wrapper
 * code runs. Detect & drop bad scripts here instead.
 *
 * Uses `new Function(src)` because it parses but does not execute the body,
 * works in both Node SSR and the browser, and rejects every common malformed-
 * CMS pattern (unterminated strings, raw newlines in literals, stray tokens).
 */
export const isValidJavaScriptSyntax = (src: string): boolean => {
  if (!src.trim()) return true;
  try {
    // eslint-disable-next-line no-new-func
    new Function(src);
    return true;
  } catch (e) {
    if (e instanceof SyntaxError) return false;
    // Any non-SyntaxError (CSP eval block, etc.) — fall back to allowing the
    // script through rather than silently dropping it.
    return true;
  }
};

// External script/stylesheet URLs we always drop: either broken (404s or
// invalid JS) or replaced by our own implementation.
const DROPPED_EXTERNAL_SCRIPT_SRCS = new Set([
  '//cdnjs.cloudflare.com/ajax/libs/jquery/1.12.4/jquery.min.js',
  'https://cdnjs.cloudflare.com/ajax/libs/jqueryui/1.12.1/jquery-ui.min.js',
  // Legacy Camilyo bootstrap script — file does not exist in dealerportal, so
  // the browser gets the 404 HTML page and throws `SyntaxError: Unexpected
  // token '<'` while parsing it as JS.
  '/content/js/camilyo/camilyo_main.js',
]);
const DROPPED_EXTERNAL_STYLESHEET_HREFS = new Set([
  'https://cdnjs.cloudflare.com/ajax/libs/jqueryui/1.12.1/themes/base/jquery-ui.min.css',
]);
const LAZY_ONLOAD_SCRIPT_SRCS = new Set([
  '/js/plugins/jquery.ui.effect.min.js',
  '/js/plugins/jquery.ui.effect-slide.min.js',
]);
// Below-the-fold widgets that find their mount element by id/selector whenever
// they run. `afterInteractive` would also emit a high-priority preload in <head>.
const LAZY_ONLOAD_SCRIPT_PREFIXES = [
  '//reviews-broadlume.pgsdemo.com/',
  '//birdeye.com/embed/',
];
// Widgets that render only into one element, keyed by src prefix. They load
// when that element nears the viewport, so they stay out of page load.
const ON_VISIBLE_SCRIPT_TARGETS: Record<string, string> = {
  '//reviews-broadlume.pgsdemo.com/modules/reviews.umd.js': 'reviews',
  '//reviews-broadlume.pgsdemo.com/modules/reviews-list.umd.js': 'reviews-list',
};

/** Whether a CMS external script should load with the `lazyOnload` strategy. */
const isLazyOnloadScript = (schemelessSrc: string): boolean =>
  LAZY_ONLOAD_SCRIPT_PREFIXES.some((prefix) =>
    schemelessSrc.startsWith(prefix)
  );

/** The id of the element a CMS external script renders into, if it loads on visibility. */
const onVisibleTargetId = (schemelessSrc: string): string | undefined =>
  Object.entries(ON_VISIBLE_SCRIPT_TARGETS).find(([prefix]) =>
    schemelessSrc.startsWith(prefix)
  )?.[1];

const childElements = (node: Element): Element[] =>
  node.children.filter((child): child is Element => 'attribs' in child);

/** Whether any element below `node` has the tag `name`. */
const hasDescendant = (node: Element, name: string): boolean =>
  childElements(node).some(
    (child) => child.name === name || hasDescendant(child, name)
  );

/** Renames every element below `node` with the tag `from` to `to`. */
const renameDescendants = (node: Element, from: string, to: string): void => {
  for (const child of childElements(node)) {
    if (child.name === from) child.name = to;
    renameDescendants(child, from, to);
  }
};

/**
 * React parser options to parse and replace any custom tags with our implemented React UI components
 */
export const reactParserOpts = ({
  children,
  partialPages,
  ...props
}: ContentReactParserProps): HTMLReactParserOptions => {
  // A set of meta tags we've seen to prevent rendering duplicate meta tags
  const seenMetaTags = new Set();
  // Anchors that renderNestedAnchor already handled, so they then render as normal anchors
  const nestedAnchorsRendered = new WeakSet<Element>();

  const withLifecycle = makeWithLifecycle(props.annotateScriptLifecycle);
  /**
   * Removes "!important" from inline style attributes of the given DOM element.
   * @param {Element} dom - The DOM element to modify.
   * @returns {JSX.Element|null} - The modified DOM element as a JSX element, or null if no modifications were made.
   */
  function RemoveImportantFromInlineStyles(dom: Element) {
    const { attribs, children } = dom;
    const attrProps = attributesToProps(attribs) as any;

    if (attrProps['style'] != null) {
      const keys = Object.keys(attrProps['style']);
      let flag = false;
      for (const key of keys) {
        if (attrProps['style'][key].includes('!important')) {
          attrProps['style'][key] = attrProps['style'][key]
            .replaceAll('!important', '')
            .trim();
          flag = true;
        }
      }
      if (flag) {
        const Tag = dom.tagName;
        return React.createElement(
          Tag,
          attrProps,
          children.length > 0 ? domToReact(children as any, opts(false)) : null
        );
      }
      return null;
    }
  }
  function RemoveOldNamePrefix(dom: Element) {
    const { attribs, name } = dom;
    if (name === 'a' && attribs['name']) {
      attribs['name'] = attribs['name'].replace('_old', ''); // Remove "_old" from the name attribute
    }

    return dom;
  }

  /** Drop or lazy-load known external scripts/stylesheets by URL. */
  function handleExternalResource(
    name: string,
    attribs: Record<string, string>,
    attrProps: any
  ) {
    if (name === 'script' && DROPPED_EXTERNAL_SCRIPT_SRCS.has(attribs['src'])) {
      // biome-ignore lint/complexity/noUselessFragments: html-react-parser drops the node
      return <></>;
    }
    if (
      name === 'link' &&
      DROPPED_EXTERNAL_STYLESHEET_HREFS.has(attribs['href'])
    ) {
      // biome-ignore lint/complexity/noUselessFragments: html-react-parser drops the node
      return <></>;
    }
    if (name === 'script' && attribs['src']) {
      const src = attribs['src'];
      const schemeless = src.replace(/^https?:/i, '');
      const targetId = onVisibleTargetId(schemeless);
      if (targetId != null) {
        return withLifecycle(
          <ScriptOnVisible targetId={targetId} {...attrProps} />,
          'once'
        );
      }
      if (LAZY_ONLOAD_SCRIPT_SRCS.has(src) || isLazyOnloadScript(schemeless)) {
        return withLifecycle(
          <Script strategy="lazyOnload" {...attrProps} />,
          'once'
        );
      }
    }
    if (name === 'script' && attribs['src'] === '/js/Plugins/readMore.js') {
      const scriptProps = { ...attrProps, src: '/js/plugins/readMore.js' };
      return withLifecycle(
        <Script strategy="afterInteractive" {...scriptProps} />,
        'once'
      );
    }
    return undefined;
  }

  /**
   * Handles <img> tags: converts falsealt -> alt, then optimizes Cloudinary
   * images and images with #size fragments. Returns undefined for images that
   * don't match any special case (they fall through to default rendering).
   */
  function handleImage(attribs: Record<string, string>, attrProps: any) {
    if (attribs?.['falsealt']) {
      attribs['alt'] = attribs['falsealt'];
      delete attribs['falsealt'];
    }

    if (ParsedCloudinaryURL.isCloudinaryURL(attribs['src'])) {
      const parsed = new ParsedCloudinaryURL(attribs['src'], true);
      const width: number = Number.parseInt(
        parsed.GetTransformationValue('w') ?? '',
        10
      );
      const height: number = Number.parseInt(
        parsed.GetTransformationValue('h') ?? '',
        10
      );
      if (!Number.isNaN(width) && !Number.isNaN(height)) {
        parsed.AddTransformation('f_auto');
        parsed.AddTransformation('q_auto');
        // next/image rejects priority together with loading="lazy"; the CMS attribute wins.
        const priority = width > 480 && attrProps['loading'] !== 'lazy';
        return (
          <Image
            {...attrProps}
            style={{ ...attrProps['style'], height: 'auto' }}
            priority={priority}
            src={parsed.GetURL()}
            alt={attribs['alt']}
            width={width}
            height={height}
          />
        );
      }
    }

    if ((attribs['src'] ?? '').includes('#size')) {
      const dimensions = GetImageDimensionsFromURLFragment(attribs['src']);
      if (dimensions != null) {
        return (
          <Image width={dimensions[0]} height={dimensions[1]} {...attrProps} />
        );
      }
    }

    return undefined;
  }

  /**
   * Matches known inline-script content patterns to a specific <Script>
   * strategy. Falls back to a hashed <Script /> with content-derived lifecycle.
   * Must use NextJS <Script> to avoid duplicate script on the DOM.
   */
  function matchInlineScriptContent(scriptContent: string, attrProps: any) {
    if (scriptContent.includes('broadstreet.loadNetworkJS')) {
      return withLifecycle(
        <Script id="broadstreet-loader" strategy="afterInteractive">
          {scriptContent}
        </Script>,
        NAMED_ID_LIFECYCLE['broadstreet-loader']
      );
    }
    if (scriptContent.includes('var redirectURL')) {
      // Uses a plain <script> (not Next.js <Script>) so React updates the element's
      // content on every navigation. Next.js <Script> deduplicates by id and won't
      // re-inject when content changes, causing RouteChangeScriptRunner to re-execute
      // stale variables from the previously-visited page.
      // RouteChangeScriptRunner queries this script by id (script#form-name-variables)
      // so it re-executes on route changes without requiring annotateScriptLifecycle.
      return withLifecycle(
        <script
          id="form-name-variables"
          dangerouslySetInnerHTML={{ __html: scriptContent }}
        />,
        NAMED_ID_LIFECYCLE['form-name-variables']
      );
    }
    if (attrProps?.id === 'template-cp-header') {
      // Same pattern as form-name-variables: plain <script> so the SSR HTML
      // parser executes it on initial load, and reExecute can clone + re-run it
      // on client navigation via data-script-lifecycle="on-route-change".
      // Next.js <Script> would deduplicate by id and skip re-injection on nav.
      return withLifecycle(
        <script
          {...attrProps}
          dangerouslySetInnerHTML={{ __html: scriptContent }}
        />,
        NAMED_ID_LIFECYCLE['template-cp-header']
      );
    }
    if (scriptContent.includes('logoFile')) {
      return withLifecycle(
        <Script id="js-dom-eventlistener" strategy="lazyOnload">
          {scriptContent}
        </Script>,
        NAMED_ID_LIFECYCLE['js-dom-eventlistener']
      );
    }
    if (scriptContent.includes('hasrlm')) {
      return withLifecycle(
        <Script id="js-variables" strategy="afterInteractive">
          {scriptContent}
        </Script>,
        NAMED_ID_LIFECYCLE['js-variables']
      );
    }
    if (scriptContent.includes('function validateForm()')) {
      return withLifecycle(
        <Script id="nfd-validate-form" strategy="lazyOnload">
          {scriptContent}
        </Script>,
        NAMED_ID_LIFECYCLE['nfd-validate-form']
      );
    }
    if (scriptContent.includes('checkDirectCamp(form)')) {
      return withLifecycle(
        <Script id="checkDirectCamp" strategy="lazyOnload">
          {scriptContent}
        </Script>,
        NAMED_ID_LIFECYCLE.checkDirectCamp
      );
    }
    if (scriptContent.includes('var cmsDatesMacro')) {
      // biome-ignore lint/complexity/noUselessFragments: html-react-parser drops the node
      return <></>;
    }
    if (scriptContent.includes('form_0024_fld_7')) {
      // biome-ignore lint/complexity/noUselessFragments: html-react-parser drops the node
      return <></>;
    }
    if (scriptContent.includes('Swiper')) {
      // Wait until the external swiper script loads
      return withLifecycle(
        <Script id="swiper" strategy="lazyOnload" {...attrProps}>
          {scriptContent}
        </Script>,
        NAMED_ID_LIFECYCLE.swiper
      );
    }
    if (
      scriptContent.includes('addRequiredField') ||
      scriptContent.includes('addFieldToValidate')
    ) {
      return withLifecycle(
        <Script
          id="acton-validation"
          strategy="afterInteractive"
          {...attrProps}
        >
          {scriptContent}
        </Script>,
        NAMED_ID_LIFECYCLE['acton-validation']
      );
    }
    if (scriptContent?.includes('$')) {
      return withLifecycle(
        <Script
          id={HashStringInsecure(scriptContent)}
          key={HashStringInsecure(scriptContent)}
          data-cys-origin={props.cysOrigin}
          {...attrProps}
          dangerouslySetInnerHTML={{ __html: scriptContent }}
        />,
        classifyScriptLifecycle(undefined, scriptContent)
      );
    }
    if (scriptContent?.includes('DOMContentLoaded')) {
      return withLifecycle(
        <Script
          id={HashStringInsecure(scriptContent)}
          key={HashStringInsecure(scriptContent)}
          data-cys-origin={props.cysOrigin}
          {...attrProps}
          dangerouslySetInnerHTML={{ __html: scriptContent }}
        />,
        classifyScriptLifecycle(undefined, scriptContent)
      );
    }
    return withLifecycle(
      <Script
        id={HashStringInsecure(scriptContent)}
        key={HashStringInsecure(scriptContent)}
        data-cys-origin={props.cysOrigin}
        {...attrProps}
        dangerouslySetInnerHTML={{ __html: scriptContent }}
      />,
      classifyScriptLifecycle(undefined, scriptContent)
    );
  }

  /**
   * Handles <script> tags: broadstreet bootstrap, head-placed inline scripts,
   * external-src scripts, and inline scripts matched by content pattern.
   * Also drops scripts with invalid JS syntax before they crash React's commit
   * phase (DEALERPORTAL-2FQY).
   */
  function handleScript(
    dom: Element,
    attribs: Record<string, string>,
    attrProps: any
  ) {
    // broadstreet script must be before interactive so the broadstreet object
    // is available to inline scripts that reference it.
    if (attribs['src'] === BROADSTREET_INIT_SCRIPT_SRC) {
      return withLifecycle(
        <Script
          strategy="beforeInteractive"
          src={BROADSTREET_INIT_SCRIPT_SRC}
          id="broadstreet-script"
        />,
        NAMED_ID_LIFECYCLE['broadstreet-script']
      );
    }

    const src = attribs['src'];
    if (src && isGatedAnalyticsSrc(src)) {
      const loader = gatedScriptLoader(attribs);
      return withLifecycle(
        props?.placement === 'head' ? (
          <script
            dangerouslySetInnerHTML={{ __html: loader }}
            data-placement="head"
          />
        ) : (
          <Script
            id={`gated-${HashStringInsecure(src)}`}
            dangerouslySetInnerHTML={{ __html: loader }}
          />
        ),
        'once'
      );
    }

    if (props?.placement === 'head') {
      const scriptContent = textContent(dom?.childNodes);
      if (
        isJavaScriptScriptType(attribs['type']) &&
        !isValidJavaScriptSyntax(scriptContent)
      ) {
        logger
          .withMetadata({
            cysOrigin: (props as { cysOrigin?: string } | undefined)?.cysOrigin,
            placement: 'head',
            preview: scriptContent.slice(0, 120),
          })
          .warn(
            'Dropping inline <script> with invalid JavaScript syntax to prevent React commit-phase crash'
          );
        // biome-ignore lint/complexity/noUselessFragments: html-react-parser drops the node
        return <></>;
      }
      return withLifecycle(
        <script
          {...attrProps}
          dangerouslySetInnerHTML={{ __html: scriptContent }}
          data-placement="head"
        />,
        classifyScriptLifecycle(attribs['src'], scriptContent)
      );
    }

    if (src) {
      if (/cctm\.xyz\/t\.js|tctm\.co\/t\.js/.test(src)) {
        // CTM does a one-shot DOM scan to rewrite phone numbers. afterInteractive
        // ensures CTM runs after React paints the page content (same as all other
        // external scripts). data-script-lifecycle is hardcoded (not via
        // withLifecycle) so RouteChangeScriptRunner always finds it regardless
        // of annotateScriptLifecycle, and reExecute re-triggers the scan on
        // every client-side navigation.
        return (
          <Script
            strategy="afterInteractive"
            {...attrProps}
            data-script-lifecycle="on-route-change"
          />
        );
      }
      return withLifecycle(
        <Script strategy="afterInteractive" {...attrProps} />,
        'once'
      );
    }

    if (dom.childNodes.length === 0) {
      return undefined;
    }

    const scriptContent = textContent(dom.childNodes);

    if (
      isJavaScriptScriptType(attribs['type']) &&
      !isValidJavaScriptSyntax(scriptContent)
    ) {
      logger
        .withMetadata({
          cysOrigin: (props as { cysOrigin?: string } | undefined)?.cysOrigin,
          id: attribs['id'],
          preview: scriptContent.slice(0, 120),
        })
        .warn(
          'Dropping inline <script> with invalid JavaScript syntax to prevent React commit-phase crash'
        );
      // biome-ignore lint/complexity/noUselessFragments: html-react-parser drops the node
      return <></>;
    }

    return matchInlineScriptContent(scriptContent, attrProps);
  }

  /** Handles <input> tags with special names: hidden sourceurl/notes, date pickers, Act-On submit. */
  function handleInput(attrProps: any) {
    if (attrProps.type === 'hidden') {
      if (attrProps.name === 'sourceurl') return <SourceInput {...attrProps} />;
      if (attrProps.name === 'notes') return <FormNotes {...attrProps} />;
    }
    if (attrProps.name === 'date') {
      const isNFDForm = attrProps.id === 'form_0082_fld_7';

      const date = props.tokens.find(
        (x) => x.TokenName === 'datesMacro'
      )?.Replacement;
      const daysInAdvance = Number.parseInt(
        props.tokens.find((x) => x.TokenName === 'minDate')?.Replacement ?? '0',
        10
      );
      const minDate = daysInAdvance
        ? addDays(new Date(), daysInAdvance)
        : new Date();
      return (
        <DatePickerInput
          {...attrProps}
          dates={date}
          minDate={minDate}
          isNFD={isNFDForm}
        />
      );
    }

    if (attrProps.name === 'Submit') {
      const formId = attrProps.id.replace('_ao_submit_input', '');
      return <ActonSubmitButton {...attrProps} formId={formId} />;
    }
    return undefined;
  }

  /** Renders <newgallery>/<carousel> as either a FaderCarousel or a Gallery. */
  function handleGalleryOrCarousel(nodes: DOMNode[], attrProps: any) {
    const filterElements = (nodes as EnhancedDOMNode[]).filter(validateTags);

    return attrProps.presentation === 'fader' ? (
      <FaderCarousel {...(attrProps as ComponentProps<typeof FaderCarousel>)}>
        {filterElements.map((elementToRender, index) => (
          <FaderCarouselItem
            key={elementToRender.attribs?.src}
            visible={index === 0}
            {...(attrProps as React.ComponentProps<typeof FaderCarouselItem>)}
            elementToRender={elementToRender}
          />
        ))}
      </FaderCarousel>
    ) : (
      <Gallery {...(attrProps as React.ComponentProps<typeof Gallery>)}>
        {filterElements.map((elementToRender, index) => (
          <GalleryItem
            // biome-ignore lint/suspicious/noArrayIndexKey: static CMS slide order; stable attribute unavailable on <slide>; index keys prevent react-multi-carousel clone collisions
            key={
              elementToRender.attribs?.src ??
              elementToRender.attribs?.href ??
              index
            }
            {...(attrProps as React.ComponentProps<typeof GalleryItem>)}
            elementToRender={elementToRender}
          />
        ))}
      </Gallery>
    );
  }

  /** Extracts tab header + content containers from `.tab_container` markup. */
  function handleTabContainer(nodes: DOMNode[], attrProps: any) {
    const elementsToRender = nodes as EnhancedDOMNode[];
    const tabContainer = elementsToRender.find((x) =>
      x.attribs['class'].includes('tabs_container')
    );
    const containerX = elementsToRender.find((x) =>
      x.attribs['class'].includes('container_x')
    );
    const headerContainers: TabProps['headerContainers'] =
      tabContainer?.children.map((c) => ({
        className: c.attribs['class'],
        rel: c.attribs['rel'],
        content: domToReact(c.children),
      })) ?? [];
    const contentContainers: TabProps['contentContainers'] =
      containerX?.children.map((c) => ({
        className: c.attribs['class'],
        content: domToReact(c.children),
      })) ?? [];
    return (
      <TabComponent
        {...(attrProps as React.ComponentProps<typeof TabComponent>)}
        headerContainers={headerContainers}
        headerClassName={tabContainer?.attribs['class']}
        contentClassName={containerX?.attribs['class']}
        contentContainers={contentContainers}
      />
    );
  }

  /** Renders <productssearchbox> (and <a href="/search">) as ProductSearchBox. */
  function handleProductSearchBox(dom: Element, attrProps: any) {
    const imgChildNode =
      dom?.childNodes?.length > 0 ? (dom?.childNodes[0] ?? null) : null;
    let searchIconSrc = '';
    if (imgChildNode && 'attribs' in imgChildNode) {
      searchIconSrc = imgChildNode?.attribs['src'];
    }
    return (
      <ProductSearchBox
        {...({
          ...attrProps,
          imgSrc: searchIconSrc,
        } as React.ComponentProps<typeof ProductSearchBox>)}
      />
    );
  }

  /**
   * The HTML parser closes an open `<a>` when it meets another `<a>`, so the server cannot send
   * an anchor inside an anchor: the browser restructures it and the whole page fails to
   * hydrate. Send the inner anchors as `<span>` and switch to the CMS markup after hydration.
   */
  function renderNestedAnchor(dom: Element) {
    nestedAnchorsRendered.add(dom);
    const safe = dom.cloneNode(true);
    renameDescendants(safe, 'a', 'span');
    return (
      <AfterHydration fallback={domToReact([safe], opts(false))}>
        {domToReact([dom], opts(false))}
      </AfterHydration>
    );
  }

  /**
   * Handles <a> tags with special roles: swiper nav prev/next, product search
   * link, broadlume logo link, and internal /-prefixed links (Next.js Link).
   */
  function handleAnchor(
    dom: Element,
    attribs: Record<string, string>,
    attrProps: any,
    nodes: DOMNode[]
  ) {
    if (
      attribs['class']?.includes('nav') &&
      (attribs['name'] === 'next' || attribs['name'] === 'prev')
    ) {
      return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <SwiperLink {...attrProps}>{domToReact(nodes, opts(false))}</SwiperLink>
      );
    }
    if (attribs['href'] === '/search') {
      return handleProductSearchBox(dom, attrProps);
    }
    if (attribs['href'] === 'https://www.broadlume.com/') {
      const imgSrc = (
        nodes.find((c) => (c as Element).name === 'img') as Element
      )?.attribs?.['src'];
      return <BroadlumeLogo title={attribs['title']} imgSrc={imgSrc} />;
    }
    if (attribs['href']?.startsWith('/')) {
      return (
        <Link
          prefetch={false}
          {...(attrProps as React.ComponentProps<typeof Link>)}
        >
          {domToReact(nodes, opts(false))}
        </Link>
      );
    }
    return undefined;
  }

  /** Handles <link>: bundles external stylesheets when enabled, else passthrough. */
  function handleLinkTag(attrProps: any) {
    if (props.bundleExternalStyles && attrProps['rel'] === 'stylesheet') {
      return (
        <BundledExternalStyle
          {...attrProps}
          href={attrProps['href']}
          layer={props.wrapStylesInLayer}
          cysOrigin={props.cysOrigin}
        />
      );
    }
    return <link {...attrProps} />;
  }

  /** Handles <style>: optimize/minify CSS and optionally wrap in a cascade layer. */
  function handleStyleTag(dom: Element, attrProps: any) {
    let cssContent = textContent(dom.childNodes);
    cssContent = WrapCSSInLayer(
      OptimizeCSS(cssContent, props.cysOrigin),
      props.wrapStylesInLayer
    );
    return (
      <style
        id={attrProps['id']}
        dangerouslySetInnerHTML={{ __html: cssContent }}
      />
    );
  }

  function ReplaceDOMNode(
    dom: DOMNode
  ): ReturnType<Required<HTMLReactParserOptions>['replace']> {
    if (!('attribs' in dom)) return null;

    RemoveOldNamePrefix(dom);

    const { attribs, name } = dom;
    const nodes: DOMNode[] = dom['children'] as unknown as DOMNode[];
    const attrProps = attributesToProps(attribs) as any;

    if (name === 'content' && attribs['name'] === 'pageContent') {
      return children;
    }

    // Remove meta tag if we've already seen it
    // Unless `data-force-meta` attribute is present
    if (
      name === 'meta' &&
      attribs['name'] &&
      seenMetaTags.has(attribs['name']) &&
      attribs['data-force-meta'] == null
    ) {
      return null;
    }
    seenMetaTags.add(attribs['name']);

    if (
      attribs?.['visible'] &&
      !visible(attribs['visible'], props.tokens ?? [])
    ) {
      // biome-ignore lint/complexity/noUselessFragments: html-react-parser drops the node
      return <></>;
    }

    const externalResource = handleExternalResource(name, attribs, attrProps);
    if (externalResource !== undefined) return externalResource;

    if (name === 'accordion') {
      return (
        <Accordion
          {...(attrProps as React.ComponentProps<typeof Accordion>)}
          elementsToRender={nodes}
        />
      );
    }

    if (attribs['class'] === 'collapse_component_item') {
      return (
        <CollapseContainer
          {...(attrProps as React.ComponentProps<typeof CollapseContainer>)}
          elementsToRender={nodes}
        />
      );
    }

    if (name === 'logo') {
      return <Logo {...(attrProps as React.ComponentProps<typeof Logo>)} />;
    }

    if (name === 'phoneto') {
      const number = attrProps['number'];
      return number != null ? (
        <PhoneTo
          {...(attrProps as React.ComponentProps<typeof PhoneTo>)}
          number={number}
          elementsToRender={nodes}
        />
      ) : null;
    }

    if (name === 'imgmenu') {
      return (
        <ImageMenu {...(attrProps as React.ComponentProps<typeof ImageMenu>)}>
          {domToReact(nodes, opts(false))}
        </ImageMenu>
      );
    }

    if (name === 'masonrygallery') {
      const galleryItem = (nodes as EnhancedDOMNode[])
        .filter((x) => x.tagName === 'galleryitembase')
        .map((x) => x.attribs);
      return (
        <Masonrygallery
          {...(attrProps as React.ComponentProps<typeof Masonrygallery>)}
          elementsToRender={galleryItem}
        />
      );
    }

    if (
      name === 'button' &&
      dom.children &&
      (dom.children?.[0] as unknown as Text)?.data === 'Print Coupon'
    ) {
      return <PrintCouponButton />;
    }

    if (name === 'img') {
      const imgResult = handleImage(attribs, attrProps);
      if (imgResult !== undefined) return imgResult;
    }

    if (
      name === 'div' &&
      attribs['class'] &&
      attribs['class'].includes('changingData')
    ) {
      const firstChild = nodes.at(0) as EnhancedDOMNode;
      if (firstChild !== undefined) {
        firstChild.attribs['style'] = firstChild?.attribs['style']?.replace(
          'display:none',
          'display: block'
        );
      }

      return <div {...attrProps}>{domToReact(nodes, opts(false))}</div>;
    }

    if (name === 'script') {
      const scriptResult = handleScript(dom, attribs, attrProps);
      if (scriptResult !== undefined) return scriptResult;
    }

    if (name === 'input') {
      const inputResult = handleInput(attrProps);
      if (inputResult !== undefined) return inputResult;
    }

    if (name === 'featuredproducts') {
      return (
        <FeaturedProducts
          {...(attrProps as React.ComponentProps<typeof FeaturedProducts>)}
        />
      );
    }

    if (name === 'newgallery' || name === 'carousel') {
      return handleGalleryOrCarousel(nodes, attrProps);
    }

    // Client components: pass only what they render, never the full parser
    // props (tokens, masterPage, ...), which would end up in the RSC payload.
    if (name === 'accordionmenu' && nodes.length === 0) {
      return (
        <AccordionMenu
          menuItems={toClientMenuItems(props.menuItems)}
          isMobileDevice={props.isMobileDevice}
          className={props.className}
        />
      );
    }

    if (name === 'productssearchbox') {
      return handleProductSearchBox(dom, attrProps);
    }

    if (name === 'verticalmultimenu') {
      return (
        <VerticalMultiMenu
          {...(attrProps as React.ComponentProps<typeof VerticalMultiMenu>)}
          menuItems={props.menuItems}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          elementsToRender={
            JSON.parse(JSON.stringify(domToReact(nodes, opts(false)))) as any
          }
        />
      );
    }

    if (name === 'menuitembase') {
      return (
        <MenuItemBase
          attribs={attrProps}
          className={props.className}
          name={props.name}
        />
      );
    }

    if (name === 'recentproducts') {
      return (
        <RecentProducts
          {...(attrProps as React.ComponentProps<typeof RecentProducts>)}
        />
      );
    }

    if (attribs['class'] === 'tab_container') {
      return handleTabContainer(nodes, attrProps);
    }

    if (name === 'tabbedmultimenu' && nodes.length === 0) {
      return (
        <Navigation
          {...(attrProps as React.ComponentProps<typeof Navigation>)}
          menuItems={toClientMenuItems(props.menuItems, {
            entrypoint: attrProps.entrypoint,
          })}
          isMobileDevice={props.isMobileDevice}
          className={attrProps.className}
        />
      );
    }
    if (name === 'tabbedmultimenu') {
      return (
        <TabbedMultiMenu
          {...(attrProps as React.ComponentProps<typeof TabbedMultiMenu>)}
          elementsToRender={nodes}
        />
      );
    }

    if (name === 'sidepanel' || name === 'navigation') {
      return (
        <Navigation
          {...(attrProps as React.ComponentProps<typeof Navigation>)}
          menuItems={toClientMenuItems(props.menuItems, {
            entrypoint: attrProps.entrypoint,
          })}
          isMobileDevice={props.isMobileDevice}
          className={attrProps.className}
        />
      );
    }

    if (name === 'map') {
      return (
        <GoogleMap {...(attrProps as React.ComponentProps<typeof GoogleMap>)} />
      );
    }

    if (name === 'youtube') {
      return (
        <YouTube {...(attrProps as React.ComponentProps<typeof YouTube>)} />
      );
    }
    if (name === 'iframe' && attribs['src']?.includes('youtube.com/embed')) {
      return <YouTube href={attribs['src']} />;
    }
    if (name === 'iframe') {
      // Browsers parse iframe content as raw text, so rendered children never hydrate.
      return <iframe {...attrProps} />;
    }

    if (name === 'a') {
      if (!nestedAnchorsRendered.has(dom) && hasDescendant(dom, 'a')) {
        return renderNestedAnchor(dom);
      }
      const anchorResult = handleAnchor(dom, attribs, attrProps, nodes);
      if (anchorResult !== undefined) return anchorResult;
    }

    if (name === 'include') {
      const include = partialPages?.find((x) => x.name === attribs['src']);

      if (include == null) {
        return null;
      }
      return <Include {...include} {...props} partialPages={partialPages} />;
    }

    if (attribs?.['clearfloat']) {
      return (
        <div {...(attrProps as Record<string, unknown>)}>
          {domToReact(nodes, opts(false))}
          <div className="clear_div" style={{ clear: 'both', height: '0px' }} />
        </div>
      );
    }
    if (name === 'link') {
      return handleLinkTag(attrProps);
    }
    if (name === 'html' || name === 'head' || name === 'body') {
      return <div {...attrProps}>{domToReact(nodes, opts(false))}</div>;
    }
    if (name === 'style') {
      return handleStyleTag(dom, attrProps);
    }
    return RemoveImportantFromInlineStyles(dom);
  }

  function AddOriginToElement(node: React.ReactNode): React.ReactNode {
    if (props.cysOrigin == null) return node;
    const tagsToAddOriginTo = [
      'script',
      'style',
      'link',
      'meta',
      (<Script />).type,
    ];
    if (React.isValidElement(node) && tagsToAddOriginTo.includes(node.type)) {
      return React.cloneElement(node, {
        ...(node as any).props,
        'data-cysorigin': props.cysOrigin,
      });
    }
    return node;
  }

  /**
   * html-react-parser leaves inline event attributes lowercase (e.g. onclick).
   * React warns and ignores them; strip them so the warning doesn't fire.
   * cloneElement merges props, so we use createElement to build a clean element.
   */
  function stripInlineLowercaseEventHandlers(
    node: React.ReactNode
  ): React.ReactNode {
    if (!React.isValidElement(node) || typeof node.type !== 'string')
      return node;
    const element = node as React.ReactElement<Record<string, any>>;
    const lowercaseEventKeys = Object.keys(element.props).filter((key) =>
      /^on[a-z]/.test(key)
    );
    if (lowercaseEventKeys.length === 0) return node;
    const filteredProps = Object.fromEntries(
      Object.entries(element.props).filter(([key]) => !/^on[a-z]/.test(key))
    );
    return React.createElement(element.type, {
      ...filteredProps,
      key: element.key,
    }) as React.ReactNode;
  }

  function wrapInClientErrorBoundary(node: React.ReactNode): React.ReactNode {
    if (!React.isValidElement(node)) return node;
    if (typeof node.type === 'string') return node;
    if (node.type === (<Script />).type) return node;
    // Hoist the parser-assigned key so sibling boundaries stay keyed.
    return (
      <ClientErrorBoundary key={node.key}>{node as any}</ClientErrorBoundary>
    ) as any;
  }

  /**
   * Wraps inline script content in a try/catch so client-side errors get
   * tagged with the origin (cysorigin) and re-thrown for Sentry to capture.
   */
  function wrapScriptContentInTryCatch(node: React.ReactNode): React.ReactNode {
    if (!React.isValidElement(node)) return node;
    const isScript = node.type === (<Script />).type || node.type === 'script';
    if (!isScript) return node;
    const element = node as React.ReactElement<Record<string, any>>;
    const hasContent =
      element.props.children != null ||
      element.props['dangerouslySetInnerHTML'] != null;
    if (!hasContent) return node;
    const scriptContent =
      element.props['dangerouslySetInnerHTML']?.__html ??
      element.props.children;
    return React.cloneElement(element, {
      ...element.props,
      dangerouslySetInnerHTML: {
        __html: `try {
            ${scriptContent}
          } catch (e) {
            e.message = "[Client Script Error - ${element.props['data-cysorigin']}] " + e.message;
            throw e;
          }
        `,
      },
    }) as React.ReactNode;
  }

  const opts: (useErrorBoundary: boolean) => HTMLReactParserOptions = (
    useErrorBoundary: boolean
  ) => ({
    replace: (dom) => {
      try {
        return ReplaceDOMNode(dom);
      } catch (e) {
        logger
          .withError(e)
          .warn('[RENDER] Error during component replacement:');
        return dom;
      }
    },
    transform: (node) => {
      node = AddOriginToElement(node);
      node = stripInlineLowercaseEventHandlers(node);
      if (useErrorBoundary) node = wrapInClientErrorBoundary(node);
      node = wrapScriptContentInTryCatch(node);
      // html-react-parser's transform signature narrows the return to
      // `string | void | Element`, but our composition returns React.ReactNode.
      // The extra Node.js types (boolean, number, Iterable) never appear in
      // practice, so a cast is safe here.
      return node as ReturnType<Required<HTMLReactParserOptions>['transform']>;
    },
  });
  return opts(true);
};
